import Loading from '@/components/Loading';
import { SelectionReorder } from '@/components/Modals/SetupWorkflow/SelectionReorder';
import {
  assembleToggleOptions,
  WORKFLOW_TAB_INDEX,
  workflowTabPath,
} from '@/components/Modals/SetupWorkflow/SetupWorkflow.utils';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { TeamGlossaryV1 } from '@/model/Glosary.model.ts';

const formContextPath = workflowTabPath(WORKFLOW_TAB_INDEX.OBSERVATIONS);

export const Observations: FC = () => {
  const { control } = useFormContext<SetupWorkflowFormValues>();
  const { response: observationsGlossaryResponse, loading: observationsGlossaryLoading } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'observations',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: { onError: { toast: false, slug: 'observations-glossary-load' } },
  });

  const workflowTabConfig = useWatch({
    control,
    name: 'workflowSetupForm.workflowTabConfig',
  });

  const toggleOptions = useMemo(
    () => assembleToggleOptions(workflowTabConfig ?? [], 'observations'),
    [workflowTabConfig]
  );

  const observationsData = (observationsGlossaryResponse?.body as { data: Array<TeamGlossaryV1> })?.data ?? [];
  const observations = observationsData.map((observation) => ({
    ...observation,
    id: observation.api_id,
  }));
  if (observationsGlossaryLoading) {
    return <Loading />;
  }
  return (
    <>
      <Controller
        name={`${formContextPath}.preSelectedObservationTypeIds`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <SelectionReorder
            selectionTitle="observation"
            options={observations}
            selection={value}
            selectionChange={(selection) => onChange(selection)}
          />
        )}
      />
      <div className="pa3 bt b-moon-gray flex justify-between items-center w-100">
        <label className="mb0 f6">After saving</label>
        <Controller
          defaultValue="focus-search"
          name={`${formContextPath}.onSave`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <select className="mw5 mb0" value={value} onChange={onChange}>
              <option value="focus-search" data-testid="after-saving-option-focus-search-bar">
                Focus search bar
              </option>
              {Object.keys(toggleOptions).map((k) => (
                <option
                  key={k}
                  value={toggleOptions[k].value}
                  data-testid={`after-saving-option-${toggleOptions[k].value}`}
                >
                  {toggleOptions[k].name}
                </option>
              ))}
              <option value="next-subject" data-testid="after-saving-option-move-to-next-animal">
                Move to next animal
              </option>
            </select>
          )}
        />
      </div>
    </>
  );
};
